import React, { useState } from 'react';
import {
  IAvailableWorkflowActions,
  ISubmissionMetadata
} from '@publishing-engine/domain/models';
import { FundingGenericDisplay } from './FundingGenericDisplay';
import { FundingGenericEmptyState } from './FundingGenericEmptyState';
import { Flex } from '@chakra-ui/react';
import FundingGenericUpdateModal from './FundingGenericUpdateModal';
import { FieldHeader } from '../common/FieldHeader';
import { FundingGenericDiffModal } from './FundingGenericDiffModal';
import { FormFieldFundingGeneric } from '@publishing-engine/domain/types/article';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';

type IProps = {
  formFieldDefinition: FormFieldFundingGeneric;
  value?: ISubmissionMetadata['fundingGeneric'];
  previousValue?: ISubmissionMetadata['fundingGeneric'];
  isEditable?: boolean;
  availableActions: IAvailableWorkflowActions;
  onChange: (value: ISubmissionMetadata['fundingGeneric']) => void;
  entryPoint: EntryPoint;
  isUpdateRequired?: boolean;
};

export const FundingGeneric: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  isEditable,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated } = useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  const hasValue = value?.exists;

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && (
        <FundingGenericUpdateModal
          formField={formFieldDefinition}
          isOpen={isEditing}
          value={value}
          onClose={() => {
            setIsEditing(false);
            onchange;
          }}
          onSubmit={value => {
            setIsEditing(false);
            onChange(value.metadata.fundingGeneric);
          }}
        />
      )}

      {isUpdated && showDiffModal ? (
        <FundingGenericDiffModal
          config={formFieldDefinition}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label="Funding Information"
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        sectionHeadingTextStyle="headingItem"
        isUpdateRequired={isUpdateRequired}
      />

      {hasValue ? (
        <FundingGenericDisplay
          value={value.value}
          exists={
            formFieldDefinition?.params.labels.options[value.exists]?.main || ''
          }
        />
      ) : (
        <FundingGenericEmptyState
          fieldLabels={formFieldDefinition.params.labels}
        />
      )}
    </Flex>
  );
};
